import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { SidebarModule } from 'primeng/sidebar';
import type { EditGroupActivity, EditGroupMember, SportOption } from '../../models/activities-management.model';

@Component({
  selector: 'bo-activity-preview-drawer',
  imports: [
    TranslateModule,
    CommonModule,
    DropdownModule,
    FormsModule,
    InputTextModule,
    InputTextareaModule,
    SidebarModule,
  ],
  templateUrl: './activity-preview-drawer.component.html',
  styleUrl: './activity-preview-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityPreviewDrawerComponent {
  // These labels used to be English literals returned from the methods below,
  // so the drawer showed English inside an otherwise French back office.
  constructor(private readonly translate: TranslateService) {}

  @Input() visible = false;
  @Input() skipEnterAnimation = false;
  @Input() coveredByDialog = false;
  @Input() activityDraft: EditGroupActivity | null = null;
  @Input() activityLevel = 'all';
  @Input() sportOptions: SportOption[] = [];
  @Input() levelOptions: SportOption[] = [];
  @Input() selectedSportValue = '';
  @Input() coverPhotoSrc = '';
  @Input() hasCoverPhoto = false;
  @Input() coverPhotoMenuVisible = false;
  @Input() editingName = false;
  @Input() editingDescription = false;
  @Input() editingRouteUrl = false;
  @Input() editingSport = false;
  @Input() editingLevel = false;
  @Input() editActionMode = false;
  @Input() canSave = false;
  @Input() operationErrorMessage = '';
  @Input() discardSuccessVisible = false;
  @Input() saveSuccessVisible = false;
  @Input() createdOn = '';
  @Input() scheduleLabel = '';
  @Input() startDate = '';
  @Input() endDate = '';
  @Input() durationLabel = '';
  @Input() hostEmail = '';
  @Input() participantTotal = 0;
  @Input() participants: EditGroupMember[] = [];
  @Input() participantsExpanded = false;
  @Input() participantsMoreCount = 0;
  @Input() canToggleParticipants = false;
  @Input() participantsLoading = false;
  @Input() getSportIconPath: (sport: string) => string = () => '';
  @Input() backButtonIcon = 'pi-arrow-right';
  @Input() backButtonLabel = 'Back to previous drawer';
  @Input() dismissible = false;

  @Output() shown = new EventEmitter<void>();
  @Output() hidden = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() bodyScrolled = new EventEmitter<Event>();
  @Output() draftChanged = new EventEmitter<void>();
  @Output() operationErrorDismissed = new EventEmitter<void>();
  @Output() discardSuccessDismissed = new EventEmitter<void>();
  @Output() saveSuccessDismissed = new EventEmitter<void>();
  @Output() photoMenuToggled = new EventEmitter<Event>();
  @Output() photoMenuClosed = new EventEmitter<void>();
  @Output() coverPhotoSelected = new EventEmitter<Event>();
  @Output() coverPhotoError = new EventEmitter<Event>();
  @Output() coverPhotoRemoved = new EventEmitter<void>();
  @Output() editNameRequested = new EventEmitter<void>();
  @Output() editDescriptionRequested = new EventEmitter<void>();
  @Output() editRouteUrlRequested = new EventEmitter<void>();
  @Output() editSportRequested = new EventEmitter<void>();
  @Output() editLevelRequested = new EventEmitter<void>();
  @Output() undoNameRequested = new EventEmitter<void>();
  @Output() undoDescriptionRequested = new EventEmitter<void>();
  @Output() undoRouteUrlRequested = new EventEmitter<void>();
  @Output() undoSportRequested = new EventEmitter<void>();
  @Output() undoLevelRequested = new EventEmitter<void>();
  @Output() sportChanged = new EventEmitter<string>();
  @Output() levelChanged = new EventEmitter<string>();
  @Output() toggleParticipants = new EventEmitter<void>();
  @Output() deleteRequested = new EventEmitter<void>();
  @Output() discardRequested = new EventEmitter<void>();
  @Output() saveRequested = new EventEmitter<void>();

  openPhotoPicker(input: HTMLInputElement) {
    this.photoMenuClosed.emit();
    input.value = '';
    input.click();
  }

  getInitials(name: string): string {
    return name.trim().charAt(0).toUpperCase();
  }

  getParticipantAvatarUrl(participant: EditGroupMember): string | null {
    return participant.avatarUrl?.trim() || null;
  }

  getOrganizerAvatarUrl(activity: EditGroupActivity): string | null {
    return activity.hostAvatarUrl?.trim() || null;
  }

  onOrganizerAvatarError(activity: EditGroupActivity) {
    activity.hostAvatarUrl = undefined;
  }

  onParticipantAvatarError(participant: EditGroupMember) {
    participant.avatarUrl = undefined;
  }

  hasDescription(description: string | null | undefined): boolean {
    return !!description?.trim();
  }

  getDescriptionText(description: string | null | undefined): string {
    return description?.trim() || this.translate.instant('activityDrawer.noDescription');
  }

  hasRouteUrl(routeUrl: string | null | undefined): boolean {
    return !!routeUrl?.trim();
  }

  isRouteUrlValid(routeUrl: string | null | undefined): boolean {
    const value = routeUrl?.trim();
    if (!value) {
      return true;
    }

    if (!/^https?:\/\//i.test(value) || /\s/.test(value)) {
      return false;
    }

    try {
      const url = new URL(value);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  getVisibilityText(activity: EditGroupActivity | null): string {
    if (activity?.privacy === 'public') {
      return this.translate.instant('activityDrawer.visibilityEveryone');
    }
    if (activity?.privacy === 'members_only') {
      return `Member of ${activity.groupName ?? ''}`.trim();
    }
    if (activity?.privacy === 'private') {
      return this.translate.instant('activityDrawer.visibilityPrivate');
    }
    return this.translate.instant('activityDrawer.visibilityEveryone');
  }

  getDeleteButtonLabel(activity: EditGroupActivity | null): string {
    if (activity?.status === 'incoming') {
      return this.translate.instant('activityDrawer.cancelActivity');
    }
    return this.translate.instant('activityDrawer.deleteActivity');
  }

  getDeleteButtonIcon(activity: EditGroupActivity | null): string {
    if (activity?.status === 'incoming') {
      return 'pi-times-circle';
    }
    return 'pi-trash';
  }

  isCancelIcon(activity: EditGroupActivity | null): boolean {
    return activity?.status === 'incoming';
  }
}
